import { DUPE_API_URL, FeatureFlags } from '@dupe/common/constants'
import { VisualMatch } from '@dupe/common/types'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import useAuthUser from '~/src/hooks/useAuthUser'
import type { DupeInputType } from '~/src/hooks/useGetDupes'
import useGetTempUserId from '~/src/hooks/useGetTempUserId'
import useIsFeatureOn from '~/src/hooks/useIsFeatureOn'
import fetcher from '~/src/lib/fetcher'

export type GetDupesQuickResponse = {
  matches: VisualMatch[]
  visualMatchId: string
  total: number
  inputType?: DupeInputType
  isPartial: boolean
  cached: boolean
  sources: {
    width_ai: boolean
    ebay: boolean
    google_lens: boolean
    shopify: boolean
  }
}

export type UseGetDupesQuickReturn = {
  data: GetDupesQuickResponse | undefined
  isLoading: boolean
  isFetching: boolean
  error: Error | null
  invalidate: () => void
}

/**
 * Hook for fetching quick dupes results from internal sources only (Width AI + eBay).
 * Use this for fast initial results (~200ms), then use useGetDupes for complete results.
 *
 * @example
 * ```tsx
 * const { data: quickData, isLoading: quickLoading } = useGetDupesQuick(image, { hash, selectedCountry })
 * const { data: fullData, isLoading: fullLoading } = useGetDupes(image, { hash, selectedCountry })
 *
 * // Show quick results immediately, then merge/replace with full results
 * const matches = fullData?.matches ?? quickData?.matches ?? []
 * const isLoading = quickLoading && fullLoading
 * ```
 */
const useGetDupesQuick = (
  image: string | null | undefined,
  {
    hash,
    rectHash,
    selectedCountry,
    noCache,
    q,
    productCategory,
    limit,
    includeEbay: includeEbayParam,
    enabled = true,
  }: {
    hash?: string
    rectHash?: string
    selectedCountry?: string
    noCache?: boolean
    q?: string
    productCategory?: string
    limit?: number
    includeEbay?: boolean
    /** Set to false to disable the query. Defaults to true. */
    enabled?: boolean
  } = {}
): UseGetDupesQuickReturn => {
  const { authUser, loading: isLoadingAuthUser } = useAuthUser()
  const { tempUserId } = useGetTempUserId()
  const queryClient = useQueryClient()
  const { featureOn: ebaySearchEnabled } = useIsFeatureOn(
    FeatureFlags.EbaySearch
  )

  const includeEbay = includeEbayParam && ebaySearchEnabled

  const endpoint = `${DUPE_API_URL}/api/dupes/quick`
  const shouldFetch =
    enabled && !!image && !!hash && !!selectedCountry && !isLoadingAuthUser

  const queryKey = useMemo(
    () =>
      [
        'dupes-quick',
        hash,
        image,
        selectedCountry,
        rectHash,
        q,
        noCache,
        productCategory,
        limit,
        includeEbay,
      ] as const,
    [
      hash,
      image,
      selectedCountry,
      rectHash,
      q,
      noCache,
      productCategory,
      limit,
      includeEbay,
    ]
  )

  const { data, isLoading, error, isFetching } =
    useQuery<GetDupesQuickResponse>({
      queryKey,
      queryFn: async () => {
        const response = await fetcher<GetDupesQuickResponse>(endpoint, {
          method: 'GET',
          dataOrParams: {
            hash,
            rectHash,
            image,
            refresh: noCache,
            country: selectedCountry,
            productCategory,
            q,
            limit,
            includeEbay,
          },
          authUser,
          tempUserId,
        })
        return response
      },
      enabled: shouldFetch,
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
      retry: false,
    })

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey })
  }

  return {
    data,
    isLoading,
    isFetching,
    error,
    invalidate,
  }
}

export default useGetDupesQuick
